import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { CREAM, INK, LINE, MUTED, PAPER, SHADOW_CARD, SHADOW_FLOAT } from '../ui/theme.ts';
import { haptic } from '../engine/haptics.ts';

/** Pointer reorder + keyboard (arrow keys on a focused row). Answer: { order: [labels] }. */
export function DragRank({ step, content, ctx }: StepProps) {
  const key = step.answerKey!;
  const stored = ctx.answers[key] as { order?: string[] } | undefined;
  const initial = stored?.order ?? (content.items ?? []).map((i) => i.label);
  const [order, setOrder] = useState<string[]>(initial);
  const [held, setHeld] = useState<number | null>(null);
  const rowsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ctx.setAnswer(key, { order: initial });
    ctx.setCta({ label: content.cta ?? ctx.shared.ui?.next ?? 'Next', onPress: ctx.next });
  }, [step.id]);

  const apply = (next: string[]) => {
    setOrder(next);
    ctx.setAnswer(key, { order: next });
  };

  const swap = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    haptic();
    apply(next);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (held === null || !rowsRef.current) return;
    const children = [...rowsRef.current.children] as HTMLElement[];
    const target = children.findIndex((el) => {
      const r = el.getBoundingClientRect();
      return e.clientY >= r.top && e.clientY <= r.bottom;
    });
    if (target >= 0 && target !== held) {
      const next = [...order];
      const [moved] = next.splice(held, 1);
      next.splice(target, 0, moved);
      haptic();
      apply(next);
      setHeld(target);
    }
  };

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />
      <div
        ref={rowsRef}
        style={{ display: 'flex', flexDirection: 'column', gap: 10, touchAction: 'none' }}
        onPointerMove={onPointerMove}
        onPointerUp={() => setHeld(null)}
        onPointerCancel={() => setHeld(null)}
      >
        {order.map((label, i) => (
          <div
            key={label}
            tabIndex={0}
            role="listitem"
            aria-label={`${label}, position ${i + 1} of ${order.length}`}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                swap(i, -1);
              }
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                swap(i, 1);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              minHeight: 60,
              padding: '10px 14px',
              borderRadius: 20,
              background: PAPER,
              border: `2px solid ${LINE}`,
              boxShadow: held === i ? SHADOW_FLOAT : SHADOW_CARD,
              transform: held === i ? 'scale(1.02)' : 'none',
              transition: 'all .2s',
            }}
          >
            <span
              onPointerDown={(e) => {
                (e.currentTarget.parentElement as HTMLElement).setPointerCapture?.(e.pointerId);
                setHeld(i);
              }}
              aria-hidden="true"
              style={{ color: MUTED, display: 'inline-grid', gridTemplateColumns: 'repeat(2,4px)', gap: 3, cursor: 'grab', padding: 4 }}
            >
              {Array.from({ length: 6 }, (_, k) => (
                <i key={k} style={{ width: 4, height: 4, borderRadius: '50%', background: 'currentColor' }} />
              ))}
            </span>
            <span style={{ flex: 1, fontWeight: 600 }}>{label}</span>
            <span style={{ display: 'flex', gap: 4 }}>
              <button type="button" aria-label="Move up" onClick={() => swap(i, -1)} style={{ width: 32, height: 32, borderRadius: '50%', border: 0, background: CREAM, cursor: 'pointer', color: INK }}>
                <i className="ph-duotone ph-caret-up" aria-hidden="true" />
              </button>
              <button type="button" aria-label="Move down" onClick={() => swap(i, 1)} style={{ width: 32, height: 32, borderRadius: '50%', border: 0, background: CREAM, cursor: 'pointer', color: INK }}>
                <i className="ph-duotone ph-caret-down" aria-hidden="true" />
              </button>
            </span>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: CREAM, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
              {i + 1}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
